import { memo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { SelectData } from "mui-rff";
import { useField } from "react-final-form";
import { FilledTextArea } from "../../../../components/FilledTextArea";
import { countrySelectData } from "../../../../data/countriesData";
import { normalizeCountrySelectValue } from "../../utils/cleanupConfigFieldValues";
import { formatCountryDisplayValue } from "../../utils/configFieldDisplay";

const countryOptions: SelectData[] = [
  { label: "None", value: "" },
  ...countrySelectData,
];

type Props = {
  name: string;
  label: string;
  value?: unknown;
  disabled?: boolean;
};

export const CountrySelectField = memo((props: Props) => {
  const { name, label, value, disabled } = props;

  if (disabled) {
    return (
      <FilledTextArea
        value={formatCountryDisplayValue(value)}
        name={name}
        label={label}
        inputProps={{ disabled: true }}
      />
    );
  }

  return <EditableCountrySelectField name={name} label={label} />;
});

const EditableCountrySelectField = memo(
  (props: Pick<Props, "name" | "label">) => {
    const { name, label } = props;
    const { input } = useField<string | undefined>(name, {
      subscription: { value: true },
    });
    const normalizedValue = normalizeCountrySelectValue(input.value);
    const selectedOption =
      countryOptions.find((option) => option.value === normalizedValue) ??
      (input.value
        ? { label: String(input.value), value: String(input.value) }
        : countryOptions[0]);

    return (
      <Autocomplete<SelectData, false, true, false>
        value={selectedOption}
        onChange={(_, nextOption) => {
          input.onChange(nextOption?.value ?? "");
          input.onBlur();
        }}
        onBlur={input.onBlur}
        onFocus={input.onFocus}
        options={countryOptions}
        getOptionLabel={(option) => String(option.label ?? "")}
        isOptionEqualToValue={(option, selected) =>
          option.value === selected.value
        }
        autoSelect
        autoHighlight
        disableClearable
        disablePortal
        data-testid={`${name}-country-select`}
        renderInput={(params) => (
          <TextField
            {...params}
            name={input.name}
            label={label}
            variant="filled"
            inputProps={{
              ...params.inputProps,
              "data-testid": `${name}-country-input`,
            }}
          />
        )}
      />
    );
  }
);
